import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { toast } from 'sonner';
import { 
  RefreshCw, BookOpen, ArrowUpRight, ArrowDownLeft, 
  TrendingUp, TrendingDown, DollarSign, FileSpreadsheet
} from 'lucide-react';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

export default function Ledger() {
  const { api } = useAuth();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [filterDirection, setFilterDirection] = useState('all');

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    fetchLedger();
    fetchSummary();
  }, [selectedYear, selectedMonth, filterDirection]);

  const fetchLedger = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('year', selectedYear);
      params.append('month', selectedMonth);
      if (filterDirection && filterDirection !== 'all') {
        params.append('direction', filterDirection);
      }
      
      const res = await api.get(`/finance/ledger?${params.toString()}`);
      setEntries(res.data);
    } catch (error) {
      console.error('Failed to fetch ledger:', error);
      toast.error('Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await api.get(`/finance/ledger/summary?year=${selectedYear}&month=${selectedMonth}`);
      setSummary(res.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const handleGenerateJE = async () => {
    try {
      const res = await api.post('/finance/journal-entries/generate', {
        year: selectedYear,
        month: selectedMonth
      });
      toast.success(`Generated ${res.data.created_count} journal entries`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate journal entries');
    }
  };

  const getCategoryBadgeColor = (type) => {
    const colors = {
      expense: 'bg-red-100 text-red-700',
      revenue: 'bg-emerald-100 text-emerald-700',
      asset: 'bg-blue-100 text-blue-700',
      liability: 'bg-amber-100 text-amber-700',
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-4" data-testid="finance-ledger">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Inflows</p>
                  <p className="text-xl font-bold text-emerald-600">
                    ${summary.total_inflow?.toLocaleString()}
                  </p>
                </div>
                <TrendingUp className="h-6 w-6 text-emerald-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Outflows</p>
                  <p className="text-xl font-bold text-red-600">
                    ${summary.total_outflow?.toLocaleString()}
                  </p>
                </div>
                <TrendingDown className="h-6 w-6 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card className={summary.net_cash_flow >= 0 ? 'border-blue-200 bg-blue-50' : 'border-amber-200 bg-amber-50'}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Net Cash Flow</p>
                  <p className={`text-xl font-bold ${summary.net_cash_flow >= 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                    ${summary.net_cash_flow?.toLocaleString()}
                  </p>
                </div>
                <DollarSign className={`h-6 w-6 ${summary.net_cash_flow >= 0 ? 'text-blue-400' : 'text-amber-400'}`} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Entries</p>
                  <p className="text-xl font-bold text-slate-900">
                    {summary.entry_count || 0}
                  </p>
                </div>
                <BookOpen className="h-6 w-6 text-slate-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex gap-3 flex-wrap">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterDirection} onValueChange={setFilterDirection}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="All Flows" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Flows</SelectItem>
              <SelectItem value="inflow">Inflows Only</SelectItem>
              <SelectItem value="outflow">Outflows Only</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon" onClick={() => { fetchLedger(); fetchSummary(); }}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        <Button onClick={handleGenerateJE}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Generate Journal Entries
        </Button>
      </div>

      {/* Category Breakdown */}
      {summary?.by_category?.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg">
          <span className="text-sm text-slate-500 mr-2">By Category:</span>
          {summary.by_category.slice(0, 8).map((cat) => (
            <Badge key={cat.code} variant="outline" className="text-xs">
              {cat.name}: ${cat.total.toLocaleString()} ({cat.count})
            </Badge>
          ))}
        </div>
      )}

      {/* Ledger Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No ledger entries for this period</p>
          <p className="text-sm text-slate-400">Post confirmed transactions to see them here</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Date</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id} data-testid={`ledger-entry-${entry.id}`}>
                  <TableCell className="text-slate-600 whitespace-nowrap">
                    {entry.transaction_date}
                  </TableCell>
                  <TableCell className="font-medium max-w-[150px] truncate">
                    {entry.vendor}
                  </TableCell>
                  <TableCell className="text-slate-600 max-w-[200px] truncate">
                    {entry.description}
                  </TableCell>
                  <TableCell>
                    <Badge className={getCategoryBadgeColor(
                      entry.direction === 'inflow' ? 'revenue' : 'expense'
                    )}>
                      {entry.category_name}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {entry.debit > 0 ? `$${entry.debit.toLocaleString()}` : '-'}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {entry.credit > 0 ? `$${entry.credit.toLocaleString()}` : '-'}
                  </TableCell>
                  <TableCell className="text-right font-medium whitespace-nowrap">
                    <span className={`flex items-center justify-end gap-1 ${entry.direction === 'inflow' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {entry.direction === 'inflow' ? (
                        <ArrowDownLeft className="h-3 w-3" />
                      ) : (
                        <ArrowUpRight className="h-3 w-3" />
                      )}
                      ${entry.amount?.toLocaleString()}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
